import { Download, Share2, Plus, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function PWAInstallModal() {
  const { t } = useLanguage();
  const { roles } = useAuth();
  const {
    isModalVisible,
    isIOS,
    isInstallable,
    triggerInstall,
    dismissModalPermanently,
  } = usePWAInstall();

  // Determine icon based on role - internal users get purple, customers get black
  const isInternalUser = roles.some(
    (r) => r.role === "employee" || r.role === "admin" || r.role === "super_admin"
  );
  const iconSrc = isInternalUser
    ? "/icons/icon-purple-512.png"
    : "/icons/icon-black-512.png";

  if (!isModalVisible) return null;

  const handleInstall = async () => {
    await triggerInstall();
    dismissModalPermanently();
  };

  // iOS step-by-step illustrated instructions
  const iosSteps = [
    {
      icon: <Share2 className="h-6 w-6 text-blue-500" />,
      label: t(
        'اضغط على زر "مشاركة" في أسفل المتصفح',
        'Tap the "Share" button at the bottom of Safari'
      ),
    },
    {
      icon: <Plus className="h-6 w-6 text-blue-500" />,
      label: t(
        'اختر "إضافة إلى الشاشة الرئيسية"',
        'Select "Add to Home Screen"'
      ),
    },
    {
      icon: (
        <img src={iconSrc} alt="دلّالي" className="h-6 w-6 rounded-lg" />
      ),
      label: t(
        'اضغط "إضافة" — سيظهر التطبيق بين تطبيقاتك',
        'Tap "Add" — the app will appear with your apps'
      ),
    },
  ];

  return (
    <Dialog open={true} onOpenChange={() => dismissModalPermanently()}>
      <DialogContent
        className="sm:max-w-sm p-0 overflow-hidden rounded-3xl border-0"
        style={{ background: "#f5efeb" }}
        dir="rtl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("تثبيت تطبيق دلّالي", "Install Dallali App")}</DialogTitle>
          <DialogDescription>
            {t("تثبيت تطبيق دلّالي على شاشتك الرئيسية للوصول السريع لجميع خدماتك", "Install the Dallali app on your home screen for quick access to all your services")}
          </DialogDescription>
        </DialogHeader>
        {/* Close button */}
        <button
          type="button"
          onClick={dismissModalPermanently}
          className="absolute start-4 top-4 z-10 rounded-full bg-black/10 p-1.5 hover:bg-black/20 transition-colors"
          aria-label="إغلاق"
        >
          <X className="h-4 w-4 text-gray-700" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center pt-8 pb-4 px-6 text-center">
          <img
            src={iconSrc}
            alt="دلّالي"
            className="w-20 h-20 rounded-2xl shadow-lg mb-4"
          />
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            {t("ثبّت تطبيق دلّالي", "Install Dallali App")}
          </h2>
          <p className="text-sm text-gray-600">
            {t(
              "وصول سريع لجميع خدماتك مباشرة من شاشتك الرئيسية",
              "Quick access to all your services directly from your home screen"
            )}
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-black/10 mx-6" />

        {isIOS ? (
          // iOS — illustrated step-by-step guide
          <div className="px-6 py-4 space-y-3">
            {iosSteps.map((step, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/60 rounded-2xl px-4 py-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                  {step.icon}
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs font-bold text-blue-500 bg-blue-50 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-sm text-gray-800 leading-snug">{step.label}</p>
                </div>
              </div>
            ))}

            {/* Safari share icon visual hint */}
            <div className="flex items-center justify-center gap-2 pt-1 pb-2">
              <div className="flex items-center gap-1.5 bg-white/80 rounded-xl px-3 py-2 border border-gray-200">
                <Share2 className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-gray-600 font-medium">
                  {t("زر المشاركة في Safari", "Share button in Safari")}
                </span>
              </div>
            </div>

            <Button
              variant="ghost"
              onClick={dismissModalPermanently}
              className="w-full text-gray-500 text-sm"
            >
              {t("ليس الآن", "Not Now")}
            </Button>
          </div>
        ) : (
          // Android / Desktop — one-tap install
          <div className="px-6 py-5 space-y-3">
            <div className="bg-white/60 rounded-2xl px-4 py-3 text-sm text-gray-700 text-center leading-relaxed">
              {t(
                "اضغط على زر التثبيت وسيظهر التطبيق مباشرة بين تطبيقاتك",
                "Tap Install and the app will appear directly among your apps"
              )}
            </div>

            {isInstallable ? (
              <Button
                onClick={handleInstall}
                size="lg"
                className="w-full gap-2 rounded-2xl"
              >
                <Download className="h-5 w-5" />
                {t("تثبيت التطبيق", "Install App")}
              </Button>
            ) : (
              <div className="text-center text-sm text-gray-500 py-2">
                {t(
                  "التثبيت غير متاح في هذا المتصفح. استخدم Chrome أو Edge.",
                  "Install not available in this browser. Use Chrome or Edge."
                )}
              </div>
            )}

            <Button
              variant="ghost"
              onClick={dismissModalPermanently}
              className="w-full text-gray-500 text-sm"
            >
              {t("ليس الآن", "Not Now")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
